import React, { useEffect, useState } from 'react';
import NewsForm from '../Forms/NewsForm';
import { NewsFormData } from '../Forms/newsFormUtils';
import { db } from '../Firebase/firebaseConfig';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import styles from './createNews.module.css';
import { uploadImageToFirebase } from '../libs/utils';
import AdminNewsTable from './AdminTables/NewsTable'; // <- Implement similarly to AdminBlogTable
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ManageNews: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [defaultValues, setDefaultValues] = useState<NewsFormData | undefined>(undefined);

  const handleSubmit = async (data: NewsFormData) => {
    setIsLoading(true);
    setStatusMessage(isEdit ? 'Updating news...' : 'Creating news...');
    setIsError(false);

    try {
      // Upload thumbnail
      const thumbnailBase64 = typeof data.thumbnailImage === 'string'
        ? data.thumbnailImage
        : data.thumbnailImage
          ? await uploadImageToFirebase(data.thumbnailImage, 'news-images')
          : '';

      // Upload multiple images
      let imageBase64s: string[] = [];
      if (data.images && data.images.length > 0) {
        const imagePromises = Array.from(data.images).map(img =>
          typeof img === 'string' ? img : uploadImageToFirebase(img, 'news-images')
        );
        imageBase64s = await Promise.all(imagePromises);
      }

      const newsPayload = {
        title: data.title,
        heading: data.heading,
        description: data.description,
        shortdescription: data.shortDescription,
        thumbnailBase64,
        imageBase64s,
        newsImagesFirst: data.newsImagesFirst || null,
        newsImagesSecond: data.newsImagesSecond || null,
        updatedAt: serverTimestamp(),
        ...(isEdit ? {} : { createdAt: serverTimestamp() })
      };

      if (isEdit && editId) {
        const docRef = doc(db, 'news', editId);
        await updateDoc(docRef, newsPayload);
        alert('News updated successfully!');
        setStatusMessage('News updated successfully!');
      } else {
        await addDoc(collection(db, 'news'), newsPayload);
        alert('News article created successfully!');
        setStatusMessage('News article created successfully!');
      }

      // Reset form
      setDefaultValues(undefined);
      setIsEdit(false);
      setEditId(null);
    } catch (error: any) {
      setStatusMessage(`Error: ${error.message || 'Submission failed.'}`);
      alert(`Error: ${error.message || 'Submission failed.'}`);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (news: NewsFormData & { id: string }) => {
    setDefaultValues({
      title: news.title,
      heading: news.heading,
      description: news.description,
      thumbnailImage: news.thumbnailImage,
      images: news.images,
      shortDescription: news.shortDescription,
      newsImagesFirst: news.newsImagesFirst,
      newsImagesSecond: news.newsImagesSecond,
    });
    setIsEdit(true);
    setEditId(news.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetDefaultValues = () => {    
    setDefaultValues({
        title: '',
        heading: '',
        description: '',
        thumbnailImage: '',
        images: [],
        shortDescription: '',
        newsImagesFirst: null,
        newsImagesSecond: null,
    })
}

  useEffect(() => {
    if(isEdit) {
      setExpanded(true);
    }
  }, [isEdit]);

  return (
    <div className={styles.createNewsContainer}>
      <Accordion expanded={expanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => {
            setExpanded(!expanded);
            setIsEdit(false); 
            resetDefaultValues();
            }}>
          <Typography variant="h6">Add New News</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            <h2>{isEdit ? 'Edit News' : 'Create News'}</h2>
            {statusMessage && (
              <p className={`${styles.statusMessage} ${isError ? styles.statusMessageerror : styles.statusMessageSuccess}`}>
                {statusMessage}
              </p>
            )}
            <NewsForm
              onSubmit={handleSubmit}
              isLoading={isLoading}
              defaultValues={defaultValues}
            />
          </div>
        </AccordionDetails>
      </Accordion>

      <AdminNewsTable onEdit={handleEdit} />
    </div>
  );
};

export default ManageNews;
